// import { userSessionSubject } from 'hooks/use-session.hook';
import { t } from 'i18next';
import { throwError, NEVER, Observable, catchError } from 'rxjs';
import { AjaxResponse } from 'rxjs/ajax';
import { clearAccessToken } from 'services/auth';
import { useRouterMemo } from 'services/route';
import { addToast } from 'services/toast';
import { userSessionSubject } from 'services/user-permission/user-permission';
import { RequestOptions, Response } from './http.type';

export function displayError(response: any) {
  if (!response) {
    addToast({
      type: 'danger',
      description: 'ERROR_NETWORK_CONNECTION',
      title: t('ERROR_NETWORK_CONNECTION') || 'ERROR_NETWORK_CONNECTION',
    });

    return;
  }

  const rawMessage =
    response?.msg ||
    response?.message ||
    response?.error ||
    'ERROR_UNEXPECTED';

  const message = Array.isArray(rawMessage) ? rawMessage.join(', ') : rawMessage;

  addToast({
    type: 'danger',
    description: message,
    title: response?.msg ? t(message) : message,
  });
}

export const handleError = (requestOpts: RequestOptions) => (error: AjaxResponse<Response<any>>) => {
  const { response } = error;

  if (error.status === 401) {
    removeAllLoading();
    clearAccessToken();
    userSessionSubject.next(undefined);
    useRouterMemo.navigate('/auth/login');

    displayError(response);

    return NEVER;
  }

  if (error.status === 403) {
    removeAllLoading();
    useRouterMemo.navigate('/auth/forbidden');

    displayError(response);

    return NEVER;
  }

  if (requestOpts.onError === 'throwOnly') {
    return throwError(() => error);
  }

  if (requestOpts.onError === 'notifyOnly') {
    displayError(response);

    return NEVER;
  }

  displayError(response);

  return throwError(() => error);
};

export const handleErrorBlob = (requestOpts: RequestOptions) => (error: AjaxResponse<any>) => {
  if (!error?.response) {
    const errorNetwork = {
      response: {
        msg: 'ERROR_NETWORK_CONNECTION',
      },
    } as AjaxResponse<Response<any>>;

    return handleError(requestOpts)(errorNetwork);
  }

  if (error?.response?.msg) {
    return handleError(requestOpts)(error);
  }

  const reader = new FileReader();
  const obs = new Observable((observer) => {
    reader.onloadend = () => {
      try {
        error['parseResponse'] = JSON.parse(reader.result as string);
      } catch (parseErr) {
        observer.error(parseErr);

        return;
      }

      observer.error(error);
    };
  });

  reader.readAsText(error['parseResponse']);

  return obs.pipe(catchError(handleError(requestOpts)));
};

/**
 * Extract file name from content-disposition header.
 */
export function extractFileName(res: AjaxResponse<any>) {
  const contentDisposition =
    (res.xhr.getResponseHeader('Content-disposition') as string) ||
    (res.xhr.getResponseHeader('content-disposition') as string) ||
    (res.responseHeaders['content-disposition'] as string) ||
    'filename=file-download';
  const startRmIndex = contentDisposition.indexOf('filename=') + 9;
  const rawFileName = contentDisposition.slice(startRmIndex, contentDisposition.length);

  return decodeURIComponent(rawFileName);
}
